import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {faPen, faTrash} from '@fortawesome/free-solid-svg-icons';
import {LocationDataProcessMeasure} from '../../../classes/offset/LocationDataProcessMeasure';
import {ActivatedRoute} from '@angular/router';
import {ProjectMeasureService} from '../../../services/project-measure/project-measure.service';
import {ProjectService} from '../../../services/project/project.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {ProjectMeasureGraphicStateService} from '../../../services/project-measure-graphic-state/project-measure-graphic-state.service';
import {Project} from '../../../classes/project/project';
import {Unit} from '../../../enum/unit.enum';
import {LocationsFormatterService} from '../../../services/project-measure/locations-formatter.service';

@Component({
  selector: 'app-offset-form',
  templateUrl: './offset-form.component.html',
  styleUrls: ['./offset-form.component.css']
})
export class OffsetFormComponent implements OnInit {
  private _selectedLocation;

  @Output() closeCallback: EventEmitter<boolean> = new EventEmitter();

  @Input() set selectedLocation(value) {
    this._selectedLocation = value;
    this.getDataProcessMeasures(this.selectedLocation.id);
    const location = this.locations.find(l => l.id === this._selectedLocation.id);
    this.currentLocationUnit = location && location.unit && location.unit.length > 0 ? location.unit[0].value : null;
    this.onOffsetValueChange();
  }

  get selectedLocation() {
    return this._selectedLocation;
  }

  @Input() dateStart: Date;
  @Input() offsetDate?: number;
  @Input() offset?;

  showSpinner;
  disabled = false;

  faPen = faPen;
  faTrash = faTrash;

  project: Project;
  units: Unit[] = [];

  selectedLocationDataProcessMeasures: any;

  comment = '';
  offsetValue = 0;
  offsetValueModified = 0;
  invertedDirection = false;

  datePickerPatchOffset: Date;
  timePickerPatchOffset: {
    hour: number;
    minute: number;
    second: number;
  };
  datePicker: Date;
  timePicker: {
    hour: number;
    minute: number;
    second: number;
  };
  dateStartPicker: Date;
  timeStartPicker: {
    hour: number;
    minute: number;
    second: number;
  };
  dateEndPicker: Date;
  timeEndPicker: {
    hour: number;
    minute: number;
    second: number;
  };

  private isSmartOffsetsMode = false;
  private smartOffset;
  private smartOffsetDate;

  private locations = [];
  public currentLocationUnit: string;

  constructor(private route: ActivatedRoute,
              private projectMeasureService: ProjectMeasureService,
              private projectService: ProjectService,
              private translateService: TranslateService,
              private toastr: ToastrService,
              private graphStateService: ProjectMeasureGraphicStateService,
              private locationsFormatterService: LocationsFormatterService
  ) {
  }

  ngOnInit() {
    const _offsetDate = new Date(this.offsetDate);
    const date = !!this.offsetDate ? _offsetDate : this.dateStart;
    this.initDateTimePickers(date);
    if (this.offset) {
      this.isSmartOffsetsMode = true;
      this.smartOffsetDate = _offsetDate;
      this.smartOffset = this.offset;
      this.offsetValue = this.offset.future.exact;
      this.onOffsetValueChange();
    }

    this.graphStateService.locationsInitedOrChanged$.subscribe((data) => {
      this.locations = this.locationsFormatterService.getAllLocationsFlat(data);
    });
  }

  private initDateTimePickers(date?) {
    this.dateStartPicker = this.dateEndPicker = this.datePicker = this.projectMeasureService.initDatePicker(date);
    this.timeStartPicker = this.timeEndPicker = this.timePicker = this.projectMeasureService.initTimePicker(date);
  }

  switchOffsetsDirection(isInverted: boolean) {
    this.invertedDirection = isInverted;
    if (this.isSmartOffsetsMode) {
      const key = isInverted ? 'past' : 'future';
      this.offsetValue = this.smartOffset[key].exact;
      const toAddToDate = isInverted ? -1000 : +1000;
      this.initDateTimePickers(this.smartOffsetDate + toAddToDate);
    }
    this.onOffsetValueChange();
  }


  setDatePatchOffset(date: string, index: number) {
    for (const selectedLocationDataProcessMeasuresKey in this.selectedLocationDataProcessMeasures) {
      if (selectedLocationDataProcessMeasuresKey === index.toString()) {
        this.selectedLocationDataProcessMeasures[selectedLocationDataProcessMeasuresKey].selected = true;
      } else {
        this.selectedLocationDataProcessMeasures[selectedLocationDataProcessMeasuresKey].selected = false;
      }
    }
    const dateObject = new Date(date);
    this.datePickerPatchOffset = dateObject;
    this.timePickerPatchOffset = {
      hour: dateObject.getUTCHours(),
      minute: dateObject.getUTCMinutes(),
      second: dateObject.getUTCSeconds(),
    };
  }

  onOffsetValueChange() {
    const sensorLength = this._selectedLocation.length || 1;
    this.offsetValueModified = this.offsetValue * sensorLength;
  }

  deleteDataProcessMeasures(dataProcessingMeasureId) {
    if (confirm(this.translateService.instant('DELETE_OFFSET'))) {
      this.projectMeasureService.deleteLocationDataProcessMeasure(dataProcessingMeasureId).subscribe(
        () => {
          this.toastr.success(this.translateService.instant('OFFSET_DELETED'));
          this.getDataProcessMeasures(this.selectedLocation.id);
        },
      );
    }
  }

  patchDataProcessMeasures(dataProcessingMeasure) {
    const payload: LocationDataProcessMeasure = {
      type: 'offset',
      project_device_location: this.selectedLocation.id,
      comment: dataProcessingMeasure.new_comment || dataProcessingMeasure.comment
    };

    payload.effected_at = dataProcessingMeasure.val_datetime;
    payload.value = dataProcessingMeasure.new_value || dataProcessingMeasure.value;
    payload.effected_at = this.projectMeasureService.formatDateToString(this.timePickerPatchOffset, this.datePickerPatchOffset);
    payload.invertedDirection = dataProcessingMeasure.invertedDirection;

    this.projectMeasureService.patchLocationDataProcessMeasure(dataProcessingMeasure.id, payload)
      .subscribe(
        () => {
          this.toastr.success(this.translateService.instant('OFFSET_UPDATED'));
          this.getDataProcessMeasures(this.selectedLocation.id);
        },
        () => this.showSpinner = false
      );
  }

  private getDataProcessMeasures(locationId: number) {
    this.showSpinner = true;
    this.projectMeasureService.getLocationDataProcessMeasure(locationId, 'offset')
      .subscribe(
        (dataProcessMeasures) => {
          this.selectedLocationDataProcessMeasures = dataProcessMeasures || [];

          for (const selectedLocationDataProcessMeasuresKey in this.selectedLocationDataProcessMeasures) {
            this.selectedLocationDataProcessMeasures[selectedLocationDataProcessMeasuresKey].dateToDisplay =
              this.projectMeasureService.formatDateToDisplay(this.selectedLocationDataProcessMeasures[selectedLocationDataProcessMeasuresKey].date);
          }
          this.showSpinner = false;
        }
      );
  }

  submit() {
    if (Object.getOwnPropertyNames(this.selectedLocation).length === 0) {
      alert(this.translateService.instant('PLEASE_LOCATION'));
      return;
    }
    this.showSpinner = true;
    const payload: LocationDataProcessMeasure = {
      type: 'offset',
      project_device_location: this.selectedLocation.id,
    };
    const effected_at = this.projectMeasureService.formatDateToString(this.timePicker, this.datePicker);
    payload.effected_at = effected_at;
    payload.value = this.offsetValue;
    payload.comment = this.comment;
    payload.invertedDirection = this.invertedDirection;

    this.projectMeasureService.postLocationDataProcessMeasure(payload)
      .subscribe(
        () => {
          this.toastr.success(this.translateService.instant('OFFSET_CREATED'));
          if (this.isSmartOffsetsMode) {
            this.closeCallback.emit(true);
          } else {
            this.getDataProcessMeasures(this.selectedLocation.id);
          }
        },
        () => this.showSpinner = false
      );
  }
}
